import React, { useEffect, useMemo, useState } from 'react'
import { Anchor, Caption, Grid, Popover, Spinner, Table } from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { ClipboardIcon } from '@enterprise-ui/icons'
import {
  extractFileMetadataApi,
  FileViewUI,
  getFileMetadataBulkApi,
  sendFileMetrics,
  useAsset,
  userInfoSelector,
} from 'store'
import formatBytes from 'utils/byteConverter'
import { capitalize, get } from 'lodash'
import { useEnv } from '@praxis/component-runtime-env'
import { useSelector } from 'react-redux'
import './FileMetadata.scss'

const TableDataComponent = ({ fieldValue }: { fieldValue: any }) => {
  const [fieldValueArray, setFieldValueArray] = useState<any[]>([])

  useEffect(() => {
    if (Array.isArray(fieldValue)) {
      setFieldValueArray(fieldValue.length > 3 ? fieldValue.slice(0, 3) : fieldValue)
    }
  }, [fieldValue])

  if (Array.isArray(fieldValue)) {
    return (
      <>
        <ul className="hc-pl-normal">
          {fieldValueArray.map((val, idx) => {
            if (val !== null && typeof val === 'object') {
              const objKeys = Object.keys(val)
              return (
                <li key={`${objKeys[0]}-${idx}`} className="hc-mb-normal">
                  {objKeys.map((valkey, keyIdx) => (
                    <div key={`${val[valkey]}-${keyIdx}`}>
                      {/* <strong>{capitalize(`${String(valkey).replace('_', ' ')}: `)}</strong>
                    {`${val[valkey]}${keyIdx < objKeys.length - 1 ? '\n' : ''}`} */}
                      <Caption
                        above={<p className="hc-fs-min">{capitalize(`${String(valkey).replace('_', ' ')}: `)}</p>}
                        className="hc-mb-dense"
                      >
                        <p className="hc-fs-sm">{`${val[valkey]}${keyIdx < objKeys.length - 1 ? '\n' : ''}`}</p>
                      </Caption>
                    </div>
                  ))}
                </li>
              )
            } else {
              return <li key={`${val}-${idx}`}>{val}</li>
            }
          })}
        </ul>
        {fieldValueArray.length < fieldValue.length && (
          <Grid.Container align="center">
            <Grid.Item>
              <Anchor
                onClick={() => {
                  setFieldValueArray(fieldValue)
                }}
              >
                Show all
              </Anchor>
            </Grid.Item>
          </Grid.Container>
        )}
      </>
    )
  }

  return <>{`${fieldValue}`}</>
}

const FileMetadata = ({ file }: { file: FileViewUI }) => {
  const {
    fileMetadataFields,
    fileMetadataServer,
    fileMetadataResponsePath,
    galleryEventsServer,
    fileMetadataMetricsTopic,
    fileMetadataGetCallMaxCount = 15,
  } = useEnv()
  const [getMetadata, setGetMetadata] = useState(false)
  const [noMetadataError, setNoMetadataError] = useState(false)
  const [showLoader, setShowLoader] = useState(false)
  const [showInprogressMessage, setShowInprogressMessage] = useState(false)
  const [extractMetadata, setExtractMetadata] = useState(false)
  const [getMetaDataDelayed, setGetMetaDataDelayed] = useState(false)
  const [fileMetadata, setFileMetadata] = useState<any>({})
  const [extractMetaDataCalled, setExtractMetadataCalled] = useState(false)
  const [getMetadataCallCount, setGetMetadataCallCount] = useState(0)
  const { assetDetailPageAsset: asset } = useAsset()
  const userInfo = useSelector(userInfoSelector)

  const { fileId, accessUrl, fileName, fileExtension } = useMemo(() => {
    const { id, fileSources } = file || {}
    const { fileExtension, accessUrl, fileName } = Array.from(fileSources || [])?.[0] || {}
    return {
      fileId: id,
      fileName,
      fileExtension,
      accessUrl,
    }
  }, [file])

  useEffect(() => {
    if (!fileMetadata.status && getMetadata && getMetadataCallCount <= fileMetadataGetCallMaxCount) {
      const fn = async () => {
        try {
          setNoMetadataError(false)
          const response = await getFileMetadataBulkApi(fileMetadataServer, fileId)
          const data = get(response, fileMetadataResponsePath || 'data[0]') || {}
          const { status } = data || {}
          if (String(status).toLowerCase() === 'failed') {
            setNoMetadataError(true)
            setShowInprogressMessage(false)
          } else if (String(status).toLowerCase() !== 'done') {
            setGetMetaDataDelayed(true)
          } else if (String(status).toLowerCase() === 'done') {
            setFileMetadata(data)
            setShowInprogressMessage(false)
            sendFileMetrics(
              galleryEventsServer,
              fileMetadataMetricsTopic,
              fileId,
              fileName,
              fileExtension,
              formatBytes(data.file_size),
              asset,
              userInfo,
              'GET_DATA'
            )
          }
        } catch (error: any) {
          const { response } = error || {}
          const { status } = response || {}
          if (extractMetaDataCalled) {
            setGetMetaDataDelayed(true)
          } else if (status === 404 && accessUrl) {
            setExtractMetadata(true)
          } else {
            setNoMetadataError(true)
            setShowInprogressMessage(false)
          }
        }
        setGetMetadata(false)
        setGetMetadataCallCount(getMetadataCallCount + 1)
        setShowLoader(false)
      }
      fn()
    }
  }, [getMetadata, fileId, accessUrl, fileMetadata, getMetadataCallCount])

  useEffect(() => {
    if (extractMetadata && !getMetaDataDelayed && !extractMetaDataCalled) {
      const fn = async () => {
        try {
          const response = await extractFileMetadataApi(fileMetadataServer, fileId, accessUrl)
          const { data } = response || {}
          if (data) {
            setGetMetaDataDelayed(true)
            setExtractMetadataCalled(true)
            sendFileMetrics(
              galleryEventsServer,
              fileMetadataMetricsTopic,
              fileId,
              fileName,
              fileExtension,
              '',
              asset,
              userInfo,
              'EXTRACT_DATA'
            )
          }
        } catch {
          setExtractMetadata(false)
        }
      }
      fn()
    }
  }, [extractMetadata, fileId, accessUrl, extractMetaDataCalled])

  useEffect(() => {
    if (getMetaDataDelayed) {
      setShowLoader(true)
      setShowInprogressMessage(true)
      setExtractMetadata(false)
      const timer = setTimeout(() => {
        setGetMetadata(true)
        setGetMetaDataDelayed(false)
      }, 2000)
      return () => {
        if (!getMetaDataDelayed && timer) {
          clearTimeout(timer)
        }
      }
    }
  }, [getMetaDataDelayed, getMetadata])

  useEffect(() => {
    if (
      !fileMetadata.status &&
      getMetadataCallCount > fileMetadataGetCallMaxCount &&
      (getMetadata || showLoader || extractMetadata)
    ) {
      setNoMetadataError(true)
      setGetMetadata(false)
      setExtractMetadata(false)
      setShowInprogressMessage(false)
      setShowLoader(false)
    }
  }, [getMetadataCallCount, getMetadata, showLoader, extractMetadata, fileMetadata])

  return (
    <>
      <Popover
        type="clickOnly"
        location="left"
        size="expanded"
        content={
          <>
            {Object.keys(fileMetadata).length > 0 && (
              <div className="file-metadata-popover-container">
                <Table name="File metadata" alternateRowColor>
                  <Table.Body>
                    <Table.Row onClick={() => {}}>
                      <Table.Data xs={5}>File size</Table.Data>
                      <Table.Data xs={7}>{formatBytes(fileMetadata.file_size)}</Table.Data>
                    </Table.Row>
                    {Object.keys(fileMetadataFields).map((field, index) => {
                      const fieldMapValue = fileMetadataFields[field]
                      let fieldValue
                      if (Array.isArray(fieldMapValue)) {
                        fieldValue = fieldMapValue.map(mapVal => get(fileMetadata, mapVal)).filter(Boolean)[0]
                      } else {
                        fieldValue = get(fileMetadata, fieldMapValue)
                      }
                      if (
                        typeof fieldValue === 'undefined' ||
                        (Array.isArray(fieldValue) && fieldValue.filter(Boolean).length === 0)
                      ) {
                        return <></>
                      }
                      return (
                        <Table.Row key={index} onClick={() => {}}>
                          <Table.Data xs={5}>{field}</Table.Data>
                          <Table.Data xs={7}>
                            <TableDataComponent fieldValue={fieldValue} />
                          </Table.Data>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table>
              </div>
            )}
            {(getMetadata || extractMetadata || showLoader) && (
              <Grid.Container justify="center" align="center">
                <Grid.Item>
                  <Spinner />
                </Grid.Item>
              </Grid.Container>
            )}
            {showInprogressMessage && (
              <Grid.Container justify="center" align="center" className="hc-mt-normal">
                <Grid.Item>
                  <p>Metadata extraction in progress.</p>
                </Grid.Item>
              </Grid.Container>
            )}
            {noMetadataError && (
              <Grid.Container justify="center" align="center">
                <Grid.Item xs>
                  <p>No metadata found for the current file.</p>
                </Grid.Item>
              </Grid.Container>
            )}
          </>
        }
        onRequestOpen={() => {
          if (Object.keys(fileMetadata).length === 0) {
            setGetMetadata(true)
          }
        }}
        onRequestClose={() => {
          setGetMetadataCallCount(0)
          setGetMetaDataDelayed(false)
        }}
      >
        <div>
          <EnterpriseIcon className="vertical-align file-metadata-icon" icon={ClipboardIcon} size="md" />
        </div>
      </Popover>
    </>
  )
}

export default FileMetadata
